import { useMemo } from 'react';
import type { Report } from '@/models/report';
import type { Study } from '@/models/study';
import type { Template } from '@/models/template';
import type {
  MeasurementGroupDisplay,
  FindingGroupDisplay,
  NormalizedAddendum,
} from '../types/reportDetail';
import { buildSectionOrder } from '../utils/buildSectionOrder';
import { ReportHeader } from './ReportHeader';
import { AddendaIndicator } from './AddendaIndicator';
import { PatientInfoSection } from './PatientInfoSection';
import { RelevantHistorySection } from './RelevantHistorySection';
import { StudyQualitySection } from './StudyQualitySection';
import { MeasurementsSection } from './MeasurementsSection';
import { FindingsSection } from './FindingsSection';
import { ConclusionSection } from './ConclusionSection';
import { SignaturesSection } from './SignaturesSection';
import { AddendaSection } from './AddendaSection';
import { TagsSection } from './TagsSection';
import { FollowUpSection } from './FollowUpSection';
import styles from './ReportDocumentView.module.css';

interface ReportDocumentViewProps {
  report: Report | undefined;
  study: Study | undefined;
  template?: Template;
  measurementGroups: MeasurementGroupDisplay[];
  findingGroups: FindingGroupDisplay[];
  addendums: NormalizedAddendum[];
  lockedMessage?: string | null;
}

export default function ReportDocumentView({
  report,
  study,
  template,
  measurementGroups,
  findingGroups,
  addendums,
  lockedMessage,
}: ReportDocumentViewProps) {
  const signatures = report?.signature ? [report.signature] : [];

  const sections = useMemo(() => buildSectionOrder(template), [template]);

  return (
    <div className={styles.scrollable}>
      <div className={styles.page}>
        {lockedMessage && (
          <div className={styles.lockedBanner}>{lockedMessage}</div>
        )}
        <ReportHeader
          status={report?.status ?? null}
          createdAt={report?.createdAt}
          accession={study?.icid}
          facility={study?.site ?? undefined}
        />

        {addendums.length > 0 && <AddendaIndicator count={addendums.length} />}

        {sections.map((section) => {
          switch (section.sectionType) {
            case 'CONTEXT':
              return (
                <div key={section.uuid}>
                  <div className={styles.divider} />
                  <PatientInfoSection study={study} report={report} />
                  <div className={styles.divider} />
                  <RelevantHistorySection
                    indication={study?.indications}
                    cardiacHistory={study?.cardiacHistory}
                  />
                  <div className={styles.divider} />
                  <StudyQualitySection
                    studyQuality={study?.studyQuality}
                    diagnosisCode={study?.diagnosisCode}
                  />
                </div>
              );
            case 'MEASUREMENTS':
              return (
                <div key={section.uuid}>
                  <div className={styles.divider} />
                  <MeasurementsSection groups={measurementGroups} />
                </div>
              );
            case 'TAGS':
              return (
                <TagsSection key={section.uuid} tags={report?.tags ?? []} />
              );
            case 'FOLLOW_UP':
              return (
                <FollowUpSection
                  key={section.uuid}
                  followUpGroups={
                    section.followUpGroups ?? template?.followUpGroups ?? []
                  }
                  followUp={report?.followUp ?? null}
                />
              );
            case 'FINDINGS':
              return (
                <div key={section.uuid}>
                  <div className={styles.divider} />
                  <FindingsSection groups={findingGroups} />
                </div>
              );
            case 'CONCLUSION':
              return (
                <div key={section.uuid}>
                  <div className={styles.divider} />
                  <ConclusionSection
                    conclusionHtml={report?.conclusion?.value ?? ''}
                  />
                </div>
              );
            case 'SIGNATURES':
              return (
                <div key={section.uuid}>
                  <div className={styles.divider} />
                  <SignaturesSection signatures={signatures} />
                </div>
              );
            default:
              return null;
          }
        })}

        <AddendaSection addendums={addendums} />
      </div>
    </div>
  );
}
